import { Component, computed, input } from '@angular/core';
import { SensorDetailDto } from '@artemis-software/wr-api';
import SensorSyncStatusEnum = SensorDetailDto.SensorSyncStatusEnum;
import SensorCleanStatusEnum = SensorDetailDto.SensorCleanStatusEnum;
import SyncBlockedReasonEnum = SensorDetailDto.SyncBlockedReasonEnum;

@Component({
  selector: 'wr-sensor-item',
  standalone: false,
  templateUrl: './sensor-item.component.html',
  styleUrl: './sensor-item.component.scss'
})
export class SensorItemComponent {
  readonly sensorItem = input.required<SensorDetailDto>();

  readonly lastValue = computed(() => this.sensorItem().lastSensorValue);
  readonly syncStatus = computed(() => this.sensorItem().sensorSyncStatus);
  readonly cleanStatus = computed(() => this.sensorItem().sensorCleanStatus);
  readonly isActive = computed(() => this.sensorItem().isActive);
  readonly activationTooltip = computed(() => this.isActive() ? 'Aktiviert' : 'Deaktiviert');

  readonly syncBlockedReason = computed(() => this.sensorItem().syncBlockedReason);

  readonly isSyncBlocked = computed(() => {
    const reason = this.syncBlockedReason();
    return reason != null && reason !== SyncBlockedReasonEnum.NoIntegration;
  });

  readonly showCleanStatus = computed(() => this.cleanStatus() !== SensorCleanStatusEnum.Idle);

  readonly classByStatus = {
    [SensorSyncStatusEnum.Disabled]: 'disabled',
    [SensorSyncStatusEnum.Failed]:   'failed',
    [SensorSyncStatusEnum.Queued]:   'queued',
    [SensorSyncStatusEnum.Syncing]:  'syncing',
    [SensorSyncStatusEnum.Synced]:   'synced',
    [SensorSyncStatusEnum.Init]:     'init',
  } as const;

  readonly iconByStatus = {
    [SensorSyncStatusEnum.Disabled]: 'ban',
    [SensorSyncStatusEnum.Failed]:   'exclamation-triangle',
    [SensorSyncStatusEnum.Queued]:   'hourglass-half',
    [SensorSyncStatusEnum.Syncing]:  'sync-alt',
    [SensorSyncStatusEnum.Synced]:   'check-circle',
    [SensorSyncStatusEnum.Init]:     'clock',
  } as const;

  readonly tooltipByStatus = {
    [SensorSyncStatusEnum.Disabled]: 'Sensor ist deaktiviert',
    [SensorSyncStatusEnum.Failed]:   'Letzte Synchronisation fehlgeschlagen',
    [SensorSyncStatusEnum.Queued]:   'In Warteschlange zur Synchronisation',
    [SensorSyncStatusEnum.Syncing]:  'Sensor wird synchronisiert',
    [SensorSyncStatusEnum.Synced]:   'Sensor wurde erfolgreich synchronisiert',
    [SensorSyncStatusEnum.Init]:     'Sensor initialisiert',
  };

  readonly cleanClassByStatus = {
    [SensorCleanStatusEnum.Idle]:     'init',
    [SensorCleanStatusEnum.Queued]:   'queued',
    [SensorCleanStatusEnum.Cleaning]: 'cleaning',
    [SensorCleanStatusEnum.Cleaned]:  'synced',
    [SensorCleanStatusEnum.Failed]:   'failed',
  } as const;

  readonly cleanIconByStatus = {
    [SensorCleanStatusEnum.Idle]:     'broom',
    [SensorCleanStatusEnum.Queued]:   'hourglass-half',
    [SensorCleanStatusEnum.Cleaning]: 'broom',
    [SensorCleanStatusEnum.Cleaned]:  'check-circle',
    [SensorCleanStatusEnum.Failed]:   'exclamation-triangle',
  } as const;

  readonly cleanTooltipByStatus = {
    [SensorCleanStatusEnum.Idle]:     'Keine Bereinigung aktiv',
    [SensorCleanStatusEnum.Queued]:   'In Warteschlange zur Bereinigung',
    [SensorCleanStatusEnum.Cleaning]: 'Sensordaten werden bereinigt',
    [SensorCleanStatusEnum.Cleaned]:  'Sensordaten wurden bereinigt',
    [SensorCleanStatusEnum.Failed]:   'Bereinigung fehlgeschlagen',
  };

  readonly blockedReasonTooltip = {
    [SyncBlockedReasonEnum.NoIntegration]:        'Keine Integration konfiguriert – Sensor kann nicht synchronisiert werden',
    [SyncBlockedReasonEnum.MissingControlSystem]: 'Control-System-ID fehlt – Sensor ist keinem System mehr zugeordnet',
    [SyncBlockedReasonEnum.AmbiguousControlSystem]: 'Gebäude hat mehrere Messstationen – der Sensor-Block muss eine Steuerungssystem-ID auswählen',
    [SyncBlockedReasonEnum.InvalidControlSystem]: 'Steuerungssystem-ID ist keine gültige GUID – Sensor kann nicht synchronisiert werden',
    [SyncBlockedReasonEnum.MissingFunctionBlock]: 'Funktionsbaustein-ID (FBId) fehlt – Sensor kann nicht synchronisiert werden',
    [SyncBlockedReasonEnum.InvalidFunctionBlock]: 'Funktionsbaustein-ID (FBId) ist keine gültige GUID – Sensor kann nicht synchronisiert werden',
    [SyncBlockedReasonEnum.MissingFbAddress]:     'FB-Adresse fehlt – Sensor kann nicht synchronisiert werden',
    [SyncBlockedReasonEnum.AmbiguousConId]:       'Mehrere Datenpunkte an dieser FB-Adresse – bitte im Sensorformular den Datenpunkt (ConId) festlegen',
  };
}
